import React, { useState } from 'react';
import { Translations } from '../i18n/translations';
import { CrownStarIcon, WhatsAppIcon, SecurityIcon, ShippingIcon, ChevronIcon, PlusIcon, MinusIcon } from '../components/icons';
import { getProductBySlug, getProductByRef, Product as ProductType, Kit, kits } from '../data/products';
import { PlaceholderImage } from '../components/PlaceholderImage';
interface ProductProps {
  t: Translations;
  setPage: (page: 'home' | 'product', slug?: string) => void;
  slug: string | null;
  lang: 'es' | 'en';
}
export const Product: React.FC<ProductProps> = ({
  t,
  setPage,
  slug,
  lang
}) => {
  // Try to find product by slug, or kit by slug, fallback to REF 1645
  let product: ProductType | Kit | undefined = slug ? getProductBySlug(slug) : undefined;
  let isKit = false;
  if (!product && slug) {
    product = kits.find(k => k.slug === slug);
    if (product) isKit = true;
  }
  if (!product) {
    product = getProductByRef('1697')!;
  }
  const [selectedSize, setSelectedSize] = useState(!isKit && (product as ProductType).sizes.length > 0 ? (product as ProductType).sizes[0] : '');
  const [selectedColor, setSelectedColor] = useState('beige');
  const [qty, setQty] = useState(1);
  const [openAccordion, setOpenAccordion] = useState<string | null>('recommended');
  const colors = [{
    id: 'beige',
    hex: '#EDE4D3'
  }, {
    id: 'cocoa',
    hex: '#8B5A2B'
  }, {
    id: 'black',
    hex: '#1A1A1A'
  }];
  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };
  const name = product.name[lang] || product.name.es;
  const price = product.price;
  const comparePrice = isKit ? (product as Kit).originalPrice : (product as ProductType).comparePrice;
  const desc = isKit ? (product as Kit).description[lang] : (product as ProductType).longDesc[lang];
  const sizes = isKit ? [] : (product as ProductType).sizes;
  return <main className="w-full min-h-screen bg-cream pb-24 lg:pb-32" data-id="element-380">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6" data-id="element-381">
        <div className="text-[10px] uppercase tracking-widest text-gold flex items-center space-x-2" data-id="element-382">
          <button onClick={() => setPage('home')} className="hover:text-gold-dark" data-id="element-383">
            {t.product.home}
          </button>
          <span data-id="element-384">/</span>
          <span className="text-ink/60" data-id="element-385">{name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-8" data-id="element-386">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-387">
          {/* LEFT: Gallery */}
          <div className="flex flex-col space-y-4" data-id="element-388">
            <div className="product-image-wrapper aspect-square rounded-sm border border-gold/20 flex items-center justify-center relative overflow-hidden group cursor-zoom-in" data-id="element-389">
              <PlaceholderImage silhouette={product.images[0].silhouette} className="product-image absolute inset-0 w-full h-full group-hover:scale-105 transition-transform duration-500" data-id="element-390" />
            </div>
            <div className="grid grid-cols-4 gap-4" data-id="element-391">
              {product.images.map((img, i) => <div key={i} className={`aspect-square rounded-sm border flex items-center justify-center cursor-pointer transition-colors relative overflow-hidden ${i === 0 ? 'border-gold' : 'border-gold/20 hover:border-gold/50'}`} data-id="element-392">
                  <PlaceholderImage silhouette={img.silhouette} className="absolute inset-0 w-full h-full" data-id="element-393" />
                </div>)}
            </div>
          </div>

          {/* RIGHT: Info */}
          <div className="flex flex-col" data-id="element-394">
            <span className="text-xs uppercase tracking-widest text-gold mb-4" data-id="element-395">
              REF {product.ref}
            </span>
            <h1 className="text-4xl lg:text-5xl font-serif text-ink mb-4" data-id="element-396">
              {name}
            </h1>

            <div className="flex items-center space-x-2 mb-6" data-id="element-397">
              <div className="flex text-gold" data-id="element-398">
                {[...Array(5)].map((_, i) => <CrownStarIcon key={i} className="w-4 h-4" data-id="element-399" />)}
              </div>
              <span className="text-sm text-ink/60" data-id="element-400">
                (127 {t.product.reviews})
              </span>
            </div>

            <div className="flex items-center space-x-4 mb-4" data-id="element-401">
              <span className="text-3xl font-serif text-ink" data-id="element-402">${price}</span>
              {comparePrice && <span className="text-xl text-ink/40 line-through" data-id="element-403">
                  ${comparePrice}
                </span>}
            </div>

            {product.badge && <div className="inline-block bg-gold/10 text-gold text-xs uppercase tracking-widest px-3 py-1 rounded-sm font-medium mb-8 w-fit" data-id="element-404">
                {product.badge === 'best-seller' ? 'Best Seller' : product.badge === 'post-surgery' ? 'Post Surgery' : 'New'}
              </div>}

            <p className="text-ink/80 leading-relaxed mb-10 font-light" data-id="element-405">
              {desc}
            </p>

            {/* Color Selector */}
            {!isKit && !['recuperacion', 'accesorios'].includes((product as ProductType).category) && <div className="mb-8" data-id="element-406">
                  <div className="flex items-center justify-between mb-4" data-id="element-407">
                    <span className="text-sm uppercase tracking-widest text-ink/80" data-id="element-408">
                      Color
                    </span>
                  </div>
                  <div className="flex space-x-4" data-id="element-409">
                    {colors.map(c => <button key={c.id} onClick={() => setSelectedColor(c.id)} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${selectedColor === c.id ? 'ring-1 ring-offset-2 ring-gold' : ''}`} style={{
                backgroundColor: c.hex
              }} data-id="element-410" />)}
                  </div>
                </div>}

            {/* Size Selector */}
            {sizes.length > 0 && <div className="mb-10" data-id="element-411">
                <div className="flex items-center justify-between mb-4" data-id="element-412">
                  <span className="text-sm uppercase tracking-widest text-ink/80" data-id="element-413">
                    Talla / Size
                  </span>
                  <button className="text-xs text-gold underline underline-offset-4 hover:text-gold-dark" data-id="element-414">
                    {t.product.sizeGuide}
                  </button>
                </div>
                <div className="flex flex-wrap gap-3" data-id="element-415">
                  {sizes.map(s => <button key={s} onClick={() => setSelectedSize(s)} className={`w-14 h-14 flex items-center justify-center text-sm transition-all rounded-sm border ${selectedSize === s ? 'bg-gold border-gold text-ink font-medium' : 'border-gold/30 text-ink/80 hover:border-gold'}`} data-id="element-416">
                      {s}
                    </button>)}
                </div>
              </div>}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-417">
              <div className="flex items-center border border-gold/30 rounded-sm h-14" data-id="element-418">
                <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-4 text-gold hover:text-gold-dark" data-id="element-419">
                  <MinusIcon className="w-4 h-4" data-id="element-420" />
                </button>
                <span className="w-8 text-center text-ink" data-id="element-421">{qty}</span>
                <button onClick={() => setQty(qty + 1)} className="px-4 text-gold hover:text-gold-dark" data-id="element-422">
                  <PlusIcon className="w-4 h-4" data-id="element-423" />
                </button>
              </div>
              <button className="flex-1 bg-gold text-ink h-14 text-sm uppercase tracking-widest hover:bg-gold-dark transition-soft rounded-sm font-medium" data-id="element-424">
                {t.bestSellers.addToRecovery}
              </button>
            </div>

            <button className="w-full border border-gold text-gold h-14 text-sm uppercase tracking-widest hover:bg-gold hover:text-ink transition-soft rounded-sm flex items-center justify-center space-x-2 mb-12" data-id="element-425">
              <WhatsAppIcon className="w-5 h-5" data-id="element-426" />
              <span data-id="element-427">{t.product.buyWhatsapp}</span>
            </button>

            {/* Trust Badges */}
            <div className="grid grid-cols-2 gap-4 mb-12 py-6 border-y border-gold/20" data-id="element-428">
              <div className="flex items-center space-x-3 text-gold" data-id="element-429">
                <ShippingIcon className="w-6 h-6" data-id="element-430" />
                <span className="text-xs uppercase tracking-widest text-ink/80" data-id="element-431">
                  {t.trust.shipping}
                </span>
              </div>
              <div className="flex items-center space-x-3 text-gold" data-id="element-432">
                <SecurityIcon className="w-6 h-6" data-id="element-433" />
                <span className="text-xs uppercase tracking-widest text-ink/80" data-id="element-434">
                  {t.trust.payment}
                </span>
              </div>
            </div>

            {/* Accordion */}
            {!isKit && <div className="flex flex-col border-t border-gold/20" data-id="element-435">
                {[{
              id: 'recommended',
              label: t.product.recommendedFor,
              content: (product as ProductType).indicatedFor[lang].join(', ')
            }, {
              id: 'fabric',
              label: t.product.fabric,
              content: (product as ProductType).fabric[lang]
            }, {
              id: 'care',
              label: t.product.care,
              content: (product as ProductType).care[lang]
            }].map(item => <div key={item.id} className="border-b border-gold/20" data-id="element-436">
                    <button onClick={() => toggleAccordion(item.id)} className="w-full py-6 flex items-center justify-between text-left group" data-id="element-437">
                      <span className="text-sm uppercase tracking-widest text-ink group-hover:text-gold transition-colors" data-id="element-438">
                        {item.label}
                      </span>
                      <span className="text-gold" data-id="element-439">
                        {openAccordion === item.id ? <MinusIcon className="w-5 h-5" data-id="element-440" /> : <PlusIcon className="w-5 h-5" data-id="element-441" />}
                      </span>
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${openAccordion === item.id ? 'max-h-40 pb-6' : 'max-h-0'}`} data-id="element-442">
                      <p className="text-ink/70 font-light text-sm leading-relaxed" data-id="element-443">
                        {item.content}
                      </p>
                    </div>
                  </div>)}
              </div>}
          </div>
        </div>

        {/* Frequently Bought Together */}
        {!isKit && <div className="mt-32 pt-16 border-t border-gold/20" data-id="element-444">
            <h3 className="text-2xl font-serif text-ink mb-10" data-id="element-445">
              {t.product.fbt}
            </h3>
            <div className="bg-white p-8 rounded-sm border border-gold/10 flex flex-col lg:flex-row items-center gap-8" data-id="element-446">
              <div className="flex items-center gap-4 lg:gap-8 overflow-x-auto w-full lg:w-auto pb-4 lg:pb-0" data-id="element-447">
                <div className="flex flex-col items-center flex-shrink-0" data-id="element-448">
                  <div className="w-24 h-24 rounded-sm flex items-center justify-center mb-4 border border-gold relative overflow-hidden" data-id="element-449">
                    <PlaceholderImage silhouette={product.images[0].silhouette} className="absolute inset-0 w-full h-full" data-id="element-450" />
                  </div>
                  <span className="text-xs text-ink text-center max-w-[100px] line-clamp-1" data-id="element-451">
                    {name}
                  </span>
                  <span className="text-sm font-serif mt-1" data-id="element-452">${price}</span>
                </div>
                <PlusIcon className="w-6 h-6 text-gold/40 flex-shrink-0" data-id="element-453" />
                <div className="flex flex-col items-center flex-shrink-0" data-id="element-454">
                  <div className="w-24 h-24 rounded-sm flex items-center justify-center mb-4 relative overflow-hidden" data-id="element-455">
                    <PlaceholderImage silhouette="board" className="absolute inset-0 w-full h-full" data-id="element-456" />
                  </div>
                  <span className="text-xs text-ink text-center max-w-[100px]" data-id="element-457">
                    Tabla Lumbar
                  </span>
                  <span className="text-sm font-serif mt-1" data-id="element-458">$34</span>
                </div>
                <PlusIcon className="w-6 h-6 text-gold/40 flex-shrink-0" data-id="element-459" />
                <div className="flex flex-col items-center flex-shrink-0" data-id="element-460">
                  <div className="w-24 h-24 rounded-sm flex items-center justify-center mb-4 relative overflow-hidden" data-id="element-461">
                    <PlaceholderImage silhouette="board" className="absolute inset-0 w-full h-full" data-id="element-462" />
                  </div>
                  <span className="text-xs text-ink text-center max-w-[100px]" data-id="element-463">
                    Espuma Magic
                  </span>
                  <span className="text-sm font-serif mt-1" data-id="element-464">$24</span>
                </div>
              </div>

              <div className="flex-1 flex flex-col items-center lg:items-end justify-center w-full lg:w-auto mt-6 lg:mt-0 lg:ml-auto" data-id="element-465">
                <div className="flex items-end space-x-3 mb-6" data-id="element-466">
                  <span className="text-3xl font-serif text-ink" data-id="element-467">
                    ${price + 34 + 24}
                  </span>
                </div>
                <button className="w-full lg:w-auto bg-gold text-ink px-8 py-4 text-sm uppercase tracking-widest hover:bg-gold-dark transition-soft rounded-sm font-medium mb-3" data-id="element-468">
                  {t.product.buyAll}
                </button>
                <button onClick={() => setPage('home')} className="text-xs text-gold underline underline-offset-4 hover:text-gold-dark" data-id="element-469">
                  {t.product.availableAsKit}
                </button>
              </div>
            </div>
          </div>}
      </div>
    </main>;
};